import { DerivedInvestorStatus, Investment, InvestmentStage, InvestmentStageType } from './types';

/**
 * Legal forward transitions for the Investment pipeline state machine.
 * See docs/ubiquitous-language.md → "Investment Stage" and "Cancel (investor)".
 */
const LEGAL_TRANSITIONS: Record<InvestmentStageType, InvestmentStageType[]> = {
  [InvestmentStage.LinkClicked]: [InvestmentStage.DueDiligenceViewed, InvestmentStage.Expired],
  [InvestmentStage.DueDiligenceViewed]: [
    InvestmentStage.SubscriptionStarted,
    InvestmentStage.Expired,
  ],
  [InvestmentStage.SubscriptionStarted]: [
    InvestmentStage.EligibilitySubmitted,
    InvestmentStage.Rejected,
    InvestmentStage.Expired,
  ],
  [InvestmentStage.EligibilitySubmitted]: [
    InvestmentStage.AgreementSigned,
    InvestmentStage.Rejected,
    InvestmentStage.NotEligible,
    InvestmentStage.Expired,
  ],
  [InvestmentStage.AgreementSigned]: [
    InvestmentStage.DataSubmissionAcknowledged,
    InvestmentStage.Rejected,
    InvestmentStage.Expired,
  ],
  [InvestmentStage.DataSubmissionAcknowledged]: [
    InvestmentStage.PaymentMethodSelected,
    InvestmentStage.Rejected,
    InvestmentStage.Expired,
  ],
  [InvestmentStage.PaymentMethodSelected]: [
    InvestmentStage.FundsSent,
    InvestmentStage.Rejected,
    InvestmentStage.Expired,
  ],
  // Once a payment is in transit, the investor can no longer cancel — the
  // wire/crypto transfer is already on its way (docs/ubiquitous-language.md → "Cancel (investor)").
  [InvestmentStage.FundsSent]: [InvestmentStage.FundsReceived],
  [InvestmentStage.FundsReceived]: [InvestmentStage.Invested],
  [InvestmentStage.Invested]: [InvestmentStage.Redeemed],
  [InvestmentStage.Redeemed]: [],
  [InvestmentStage.Rejected]: [],
  [InvestmentStage.NotEligible]: [],
  [InvestmentStage.Expired]: [],
};

export const canTransition = (from: InvestmentStageType, to: InvestmentStageType): boolean =>
  LEGAL_TRANSITIONS[from]?.includes(to) ?? false;

/**
 * Pure transition: returns a new Investment with the stage advanced and the
 * timeline appended. Throws on an illegal transition rather than silently
 * no-opping, so callers can surface a real error instead of a stuck UI.
 */
export const applyTransition = (
  investment: Investment,
  to: InvestmentStageType,
  at: string = new Date().toISOString(),
): Investment => {
  if (!canTransition(investment.stage, to)) {
    throw new Error(`Illegal investment stage transition: ${investment.stage} -> ${to}`);
  }

  return {
    ...investment,
    stage: to,
    lastActivityAt: at,
    stageTimeline: [...investment.stageTimeline, { stage: to, at }],
  };
};

const STAGE_TO_STATUS: Partial<Record<InvestmentStageType, DerivedInvestorStatus>> = {
  [InvestmentStage.SubscriptionStarted]: 'investor eligibility',
  [InvestmentStage.EligibilitySubmitted]: 'sign agreement',
  [InvestmentStage.AgreementSigned]: 'data acknowledgment',
  [InvestmentStage.DataSubmissionAcknowledged]: 'awaiting payment',
  [InvestmentStage.PaymentMethodSelected]: 'awaiting payment',
  [InvestmentStage.FundsReceived]: 'pending units',
  [InvestmentStage.Invested]: 'completed',
  [InvestmentStage.Rejected]: 'rejected',
  [InvestmentStage.NotEligible]: 'not eligible',
  [InvestmentStage.Expired]: 'expired',
  [InvestmentStage.Redeemed]: 'redeemed',
};

/**
 * Derives the investor-facing status from stage + sub-fields. Never stored —
 * see docs/ubiquitous-language.md → "Investor-facing status".
 *
 * `FundsSent` is the one stage that isn't a 1:1 stage->status mapping: a wire
 * payment is still travelling ("wire in transit"), but a crypto payment has
 * already settled on-chain the moment it's submitted ("funds sent").
 *
 * Returns null for the pre-subscription stages (LinkClicked, DueDiligenceViewed)
 * — those describe a Pending Investor, not yet a tracked in-progress Investment.
 */
export const deriveInvestorStatus = (investment: Investment): DerivedInvestorStatus | null => {
  if (investment.stage === InvestmentStage.FundsSent) {
    return investment.paymentMethod === 'Crypto' ? 'funds sent' : 'wire in transit';
  }

  return STAGE_TO_STATUS[investment.stage] ?? null;
};

const CANCELLABLE_STATUSES: DerivedInvestorStatus[] = [
  'investor eligibility',
  'sign agreement',
  'data acknowledgment',
  'awaiting payment',
];

/**
 * Investor cancel is allowed only while the derived status is investor
 * eligibility / sign agreement / awaiting payment — forbidden once a payment
 * is in transit or later. See docs/ubiquitous-language.md → "Cancel (investor)".
 */
export const canCancelInvestment = (investment: Investment): boolean => {
  const status = deriveInvestorStatus(investment);
  return status !== null && CANCELLABLE_STATUSES.includes(status);
};

export const cancelInvestment = (
  investment: Investment,
  at: string = new Date().toISOString(),
): Investment => {
  if (!canCancelInvestment(investment)) {
    throw new Error('Investment cannot be cancelled in its current status');
  }

  return applyTransition(investment, InvestmentStage.Rejected, at);
};

/**
 * Pipeline boards don't show payment sub-steps (spec 2026-08-03): an
 * investment that picked a method but hasn't submitted payment sits in the
 * Data Submission Acknowledged column.
 */
export const getPipelineBoardStage = (stage: InvestmentStageType): InvestmentStageType =>
  stage === InvestmentStage.PaymentMethodSelected
    ? InvestmentStage.DataSubmissionAcknowledged
    : stage;
